/**
 * Tier 1 of the design system: the PRIMITIVES (docs/architecture/DESIGN_SYSTEM.md §2).
 *
 * Plain values that only a theme reads (`./theme.ts`). A member or a feature never imports this
 * file: it draws roles from `useTheme()`, so a change of value happens in one place (audit check R).
 *
 * Standard ramps, not invented ones (QD1, the user 2026-10-08): `neutral` is Material's grey,
 * which is the payment design's ink ladder made opaque (its `ink87`, `ink38` and `ink04` over white
 * are grey 900, 500 and 100); the hues are Tailwind's, which web v1 already draws from, with
 * `blue[500]` set to the POS's brand blue (v1's `activeColor`, the payment design's `cta`).
 */

export const neutral = {
  0: "#ffffff",
  50: "#fafafa",
  100: "#f5f5f5",
  200: "#eeeeee",
  300: "#e0e0e0",
  400: "#bdbdbd",
  500: "#9e9e9e",
  600: "#757575",
  700: "#616161",
  800: "#424242",
  900: "#212121",
  1000: "#000000",
} as const;

export const blue = {
  50: "#eff6ff",
  100: "#dbeafe",
  200: "#bfdbfe",
  300: "#93c5fd",
  400: "#60a5fa",
  500: "#397ef5",
  600: "#2563eb",
  700: "#1d4ed8",
  800: "#1e40af",
  900: "#1e3a8a",
} as const;

export const red = {
  50: "#fef2f2",
  100: "#fee2e2",
  200: "#fecaca",
  300: "#fca5a5",
  400: "#f87171",
  500: "#ef4444",
  600: "#dc2626",
  700: "#b91c1c",
  800: "#991b1b",
  900: "#7f1d1d",
} as const;

export const green = {
  50: "#f0fdf4",
  100: "#dcfce7",
  200: "#bbf7d0",
  300: "#86efac",
  400: "#4ade80",
  500: "#22c55e",
  600: "#16a34a",
  700: "#15803d",
  800: "#166534",
  900: "#14532d",
} as const;

export const amber = {
  50: "#fffbeb",
  100: "#fef3c7",
  200: "#fde68a",
  300: "#fcd34d",
  400: "#fbbf24",
  500: "#f59e0b",
  600: "#d97706",
  700: "#b45309",
  800: "#92400e",
  900: "#78350f",
} as const;

export const yellow = {
  50: "#fefce8",
  100: "#fef9c3",
  200: "#fef08a",
  300: "#fde047",
  400: "#facc15",
  500: "#eab308",
  600: "#ca8a04",
  700: "#a16207",
  800: "#854d0e",
  900: "#713f12",
} as const;

/** Tailwind's violet: the `highlight` status, an order's special marks (a preorder, a large order). */
export const violet = {
  50: "#f5f3ff",
  100: "#ede9fe",
  200: "#ddd6fe",
  300: "#c4b5fd",
  400: "#a78bfa",
  500: "#8b5cf6",
  600: "#7c3aed",
  700: "#6d28d9",
  800: "#5b21b6",
  900: "#4c1d95",
} as const;

/** Black at an opacity: a scrim, a shadow, a state layer over any ground. */
export const blackAlpha = (opacity: number) => `rgba(0, 0, 0, ${opacity})`;

/**
 * A category's letter in the rail: web v1 drew a RANDOM one of these per mount (Tailwind's 500s,
 * `Containers/Categories`'s `COLOR_LIST`); v2 picks one per category, stably (FLOW_AUDIT.md A11).
 * Data, not a role.
 */
export const CATEGORY_LETTER_COLORS = [
  "#A855F7", "#F43F5E", "#EC4899", "#D946EF", "#8B5CF6", "#6366F1", "#3B82F6", "#0EA5E9", "#06B6D4", "#14B8A6",
  "#10B981", "#22C55E", "#84CC16", "#EAB308", "#F59E0B", "#F97316", "#78716C", "#737373", "#71717A",
] as const;

export const SPACING = { xxs: 2, xs: 4, sm: 8, md: 12, lg: 16, xl: 20, xxl: 24, xxxl: 32 } as const;

export const RADIUS = { sm: 6, md: 8, lg: 10, xl: 12, pill: 999 } as const;

/**
 * The type scale, from what `packages/ui` draws most (2026-10-08: 14 px 180 times, 12 px 92, 13 px 85,
 * 16 px 56, 18 px 28, 20 px 17, 24 px 13). The family stays the platform's system face, which is what
 * v2 draws today (DESIGN_SYSTEM.md §2).
 */
export const FONT_SIZE = { 10: 10, 12: 12, 13: 13, 14: 14, 16: 16, 18: 18, 20: 20, 24: 24 } as const;

export const FONT_WEIGHT = { regular: "400", medium: "500", semibold: "600", bold: "700" } as const;

/** Elevations drawn as a CSS `boxShadow` (react-native-web and React Native 0.76+ both read it). */
export const SHADOWS = {
  /** MUI's dialog elevation (24), as web v1's warning modal draws it. */
  dialog:
    "0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)",
  /** react-toastify's toast. */
  toast: "0px 4px 12px rgba(0, 0, 0, 0.1)",
  /** a card lifted off its ground (v2's item card) */
  card: "0px 1px 4px rgba(0, 0, 0, 0.05)",
  /** a menu or a popover */
  menu: "0px 4px 16px rgba(0, 0, 0, 0.12)",
} as const;
